import { Button, Card, DatePicker, Select, Space, Switch } from 'antd'
import { ReloadOutlined, DownloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { useQuery } from '@tanstack/react-query'

import { departmentsApi } from '@/features/masters/departments/api'
import { machinesApi } from '@/features/masters/machines/api'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { ReportFilters } from '@/features/maintenance/api'

interface Props {
  filters: ReportFilters
  onChange: (next: ReportFilters) => void
  onRefresh?: () => void
  onExport?: () => void
  exporting?: boolean
  /** Which filter inputs to render — keeps the bar lean per report. */
  show: {
    dateRange?: boolean
    department?: boolean
    machine?: boolean
    machineType?: boolean
    frequency?: boolean
    skillType?: boolean
    maintenanceType?: boolean
    priority?: boolean
    bucket?: boolean
    utilityType?: boolean
    band?: boolean
    criticalOnly?: boolean
    abnormalOnly?: boolean
    waitingSpareOnly?: boolean
  }
}

const FREQUENCIES = ['DAILY', 'WEEKLY', 'FORTNIGHTLY', 'MONTHLY', 'QUARTERLY', 'HALF_YEARLY', 'ANNUAL']
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']
const MAINT_TYPES = ['BREAKDOWN', 'PREVENTIVE', 'EMERGENCY', 'PREDICTIVE', 'OVERHAUL', 'CALIBRATION']
const SKILL_TYPES = ['MECHANICAL', 'ELECTRICAL', 'UTILITY', 'GENERAL']
const AGEING_BUCKETS = ['0-2h', '2-8h', '8-24h', '1-3d', '3d+']
const UTILITY_TYPES = [
  'COMPRESSOR', 'HUMIDIFICATION', 'DG_SET', 'ELECTRICAL_PANEL',
  'CHILLER', 'BOILER', 'WATER',
]
const HEALTH_BANDS = ['HEALTHY', 'WATCH', 'UNDER_OBSERVATION', 'CRITICAL', 'STOPPED']

/**
 * Shared filter bar across all maintenance reports. ENTER ONCE → AUTO FETCH:
 * the parent owns the `filters` state and re-renders the report whenever
 * any control changes.
 */
export default function ReportFilterBar({
  filters, onChange, onRefresh, onExport, exporting, show,
}: Props) {
  const { plantId } = usePlantId()

  const { data: machinesData } = useQuery({
    queryKey: ['machines-for-reports', plantId],
    enabled: !!plantId && !!show.machine,
    queryFn: () => machinesApi.list({ plant_id: plantId, is_active: true, page: 1, page_size: 500 }),
  })
  const machines = machinesData?.items ?? []

  const { data: deptsData } = useQuery({
    queryKey: ['departments-all'],
    enabled: !!show.department,
    queryFn: () => departmentsApi.list({ page: 1, page_size: 200 }),
  })
  const depts = deptsData?.items ?? []

  const dateRange = filters.from_date && filters.to_date
    ? [dayjs(filters.from_date), dayjs(filters.to_date)] as [dayjs.Dayjs, dayjs.Dayjs]
    : undefined

  const set = (patch: Partial<ReportFilters>) => onChange({ ...filters, ...patch })

  return (
    <Card size="small" style={{ marginBottom: 12 }}>
      <Space wrap size={[8, 8]}>
        {show.dateRange && (
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(v) => set({
              from_date: v?.[0]?.format('YYYY-MM-DD'),
              to_date:   v?.[1]?.format('YYYY-MM-DD'),
            })}
          />
        )}
        {show.department && (
          <Select
            allowClear placeholder="Department" style={{ width: 200 }}
            value={filters.department_id}
            options={depts.map((d) => ({ label: d.name, value: d.id }))}
            onChange={(v) => set({ department_id: v })}
          />
        )}
        {show.machine && (
          <Select
            allowClear showSearch placeholder="Machine" style={{ width: 220 }}
            value={filters.machine_id}
            options={machines.map((m) => ({ label: `${m.machine_id_str} — ${m.name}`, value: m.id }))}
            filterOption={(i, o) => (o?.label as string).toLowerCase().includes(i.toLowerCase())}
            onChange={(v) => set({ machine_id: v })}
          />
        )}
        {show.machineType && (
          <Select
            allowClear placeholder="Machine type" style={{ width: 180 }}
            value={filters.machine_type}
            options={[...new Set(machines.map((m) => m.section))].map((s) => ({ label: s, value: s }))}
            onChange={(v) => set({ machine_type: v })}
          />
        )}
        {show.frequency && (
          <Select
            allowClear placeholder="Frequency" style={{ width: 150 }}
            value={filters.frequency}
            options={FREQUENCIES.map((f) => ({ label: f, value: f }))}
            onChange={(v) => set({ frequency: v })}
          />
        )}
        {show.skillType && (
          <Select
            allowClear placeholder="Skill type" style={{ width: 150 }}
            value={filters.skill_type}
            options={SKILL_TYPES.map((s) => ({ label: s, value: s }))}
            onChange={(v) => set({ skill_type: v })}
          />
        )}
        {show.maintenanceType && (
          <Select
            allowClear placeholder="Maint. type" style={{ width: 160 }}
            value={filters.maintenance_type}
            options={MAINT_TYPES.map((m) => ({ label: m, value: m }))}
            onChange={(v) => set({ maintenance_type: v })}
          />
        )}
        {show.priority && (
          <Select
            allowClear placeholder="Priority" style={{ width: 130 }}
            value={filters.priority}
            options={PRIORITIES.map((p) => ({ label: p, value: p }))}
            onChange={(v) => set({ priority: v })}
          />
        )}
        {show.bucket && (
          <Select
            allowClear placeholder="Ageing bucket" style={{ width: 150 }}
            value={filters.bucket}
            options={AGEING_BUCKETS.map((b) => ({ label: b, value: b }))}
            onChange={(v) => set({ bucket: v })}
          />
        )}
        {show.utilityType && (
          <Select
            allowClear placeholder="Utility" style={{ width: 180 }}
            value={filters.utility_type}
            options={UTILITY_TYPES.map((u) => ({ label: u, value: u }))}
            onChange={(v) => set({ utility_type: v })}
          />
        )}
        {show.band && (
          <Select
            allowClear placeholder="Health band" style={{ width: 180 }}
            value={filters.band}
            options={HEALTH_BANDS.map((b) => ({ label: b, value: b }))}
            onChange={(v) => set({ band: v })}
          />
        )}
        {show.criticalOnly && (
          <Switch
            checked={!!filters.critical_only}
            onChange={(v) => set({ critical_only: v })}
            checkedChildren="Critical only" unCheckedChildren="All"
          />
        )}
        {show.abnormalOnly && (
          <Switch
            checked={!!filters.abnormal_only}
            onChange={(v) => set({ abnormal_only: v })}
            checkedChildren="Abnormal only" unCheckedChildren="All"
          />
        )}
        {show.waitingSpareOnly && (
          <Switch
            checked={!!filters.waiting_spare_only}
            onChange={(v) => set({ waiting_spare_only: v })}
            checkedChildren="Waiting spare" unCheckedChildren="All"
          />
        )}
        {onRefresh && <Button icon={<ReloadOutlined />} onClick={onRefresh}>Refresh</Button>}
        {onExport && (
          <Button icon={<DownloadOutlined />} loading={exporting} onClick={onExport}>
            Export CSV
          </Button>
        )}
      </Space>
    </Card>
  )
}
